import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
    IonContent, IonToolbar, IonButtons, IonButton, IonCol, IonItem, IonThumbnail, IonIcon, IonLabel, IonGrid,
    IonRow, IonText, IonCard, IonCardContent, IonNote, useIonAlert,
    IonList, IonLoading, IonHeader, IonModal, IonTitle, IonPage, useIonModal,
} from '@ionic/react';
import {
    qrCodeOutline, arrowForwardOutline, appsOutline, repeatOutline, cash, closeOutline,
} from 'ionicons/icons';
import _ from "lodash";
import { Networks } from '@stellar/stellar-sdk';
import SumsubWebSdk from '@sumsub/websdk-react';
import { ebioroApi, useGetUserInfoQuery } from "../../services/api/ebioro/ebioro";
import { useSep10Query, useAccountDetailsQuery } from '../../services/api/stellar';
import QrCodePageStellarToken from '../qr_code/QrCodePageStellarToken';
import { useAppSelector, useAppDispatch } from '../../services/redux/hook';
import { decimalAdjust } from "../../services/utils/decimalAdjust";
import { formatConversion } from "../../services/utils/formatConversion";
import { Capacitor } from "@capacitor/core";
import SumsubPlugin from "../../services/sumsubPlugin/sumsubPlugin";
import UnsupportedRegion from "../../components/unsupportedRegion-Modal/UnsupportedRegion";
import CryptoProvidersPage from "../crypto-providers/CryptoProvidersPage";
import TradePage from "../trade/TradePage";
import './AccountDetailsPage.css';
import { Asset, BuyWidgetParams, Operation, SellWidgetParams } from "../../types/types";
import RecipientPage from "../recipients/RecipientPage";
import { resetPaymentDestinationInfo } from "../../services/redux/slices/paymentDestinationInfo/paymentDestinationInfo";
import MarketInfo from "./MarketInfo";
import { closeModal } from "../../services/redux/slices/opened_navigation_modal/opened_root_modal";
import useCryptoMode from "../../hooks/useCryptoMode";
import { Provider } from "react-redux";
import { store } from "../../services/redux/store";
import { Browser } from '@capacitor/browser';
import * as Sentry from '@sentry/react';

const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';
const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;

const Page: React.FC = () => {

    const nav = document.querySelector("ion-nav");
    const pageRef = useRef();
    const { cryptoMode } = useCryptoMode();
    const [isSpinner, setIsSpinner] = useState(false);


    const close = () => {

        dispatch(closeModal());

    }

    const [showQRReceive, dismissQRReceive] = useIonModal(<QrCodePageStellarToken closeAction={() => dismissQRReceive()} />);

    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();

    const [present] = useIonAlert();

    const asset: Asset = useAppSelector((state) => state.selectedAsset);

    const [showLoading, setShowLoading] = useState(false);

    const walletInfo = useAppSelector((state) => state.walletInfo);
    const [unsupportedRegionPage, setShowUnsupportedRegionPage] = useState(false);
    const unsupportedRegionModal = useRef<HTMLIonModalElement>(null);

    const params = {
        account: walletInfo.account_key as string,
        home_domain: ebioroAuthDomain,
        network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
        privateKey: walletInfo.device_private_key as string
    }

    const registrationLevel = import.meta.env.VITE_KYC_REGISTRATION_LEVEL_FULL;
    const [kycToken, setKycToken] = useState('');
    const [kycTestEnvironment, setKycTestEnvironment] = useState(false);
    const [verificationConfig, setVerificationConfig] = useState({});

    const [showVerificationScreen, setShowVerificationScreen] = useState(false);
    const verificationScreenModal = useRef<HTMLIonModalElement>(null);

    const sep10Tokens = useSep10Query(params, { selectFromResult: result => ({ data: result.data ? result.data[ebioroAuthDomain] : undefined }) })

    const authToken = sep10Tokens.data

    const assetBalanceData = useAccountDetailsQuery(walletInfo.account_key, { selectFromResult: result => ({ data: result?.data?.details.balances[asset.identifier] ? result.data.details.balances[asset.identifier].available.toNumber() : 0 }) });

    const assetBalance = decimalAdjust(assetBalanceData.data, -asset.decimal_places)

    const { data: userInfo } = useGetUserInfoQuery({ skip: (authToken === undefined) });

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.message}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],
        })

    }

    const quotes = useAppSelector((state) => state.quotes.quotes);

    const assetFiatQuote = quotes[asset.coingecko_id][userInfo?.currency_code.toLocaleLowerCase() || 'usd'];

    const supportedAssets = useAppSelector((state) => state.supportedAssets.supportedAssets);


    const findAssetToTrade = (): Asset | null => {

        const filteredAssets = supportedAssets.filter((a: Asset) => {
            if (cryptoMode) {
                return (a.type === 'crypto' || a.type === 'fiat') && a.code !== asset.code;
            } else {
                return a.type === 'fiat' && a.code !== asset.code;
            }
        }).filter((a: Asset) => a.trade_enabled === true);

        // Return the first relevant asset if found, or null if no asset is found
        return filteredAssets.length > 0 ? filteredAssets[0] : null;
    };


    const OperationComponent: React.FC<{ closeAction: Function }> = ({ closeAction }) => {

        const assetToTrade = findAssetToTrade();

        return (
            <>

                <IonContent scrollY={false} >
                    <IonCard>
                        <IonCardContent>
                            <IonList className="bg-transparent">

                                <IonItem
                                    hidden={!asset.buy_enabled}
                                    lines='none'
                                    onClick={() => presentBuyPage()}>

                                    <IonIcon
                                        slot="start"
                                        src={cash}
                                        size='large'
                                        color={'secondary'} />
                                    <IonLabel>
                                        <IonText className="ion-text-wrap">

                                            <h2>
                                                {t('operations.Buy.Label')}
                                            </h2>
                                        </IonText>
                                        <IonNote>
                                            {t('operations.Buy.Label.description')}
                                        </IonNote>

                                    </IonLabel>
                                </IonItem>

                                <IonItem
                                    hidden={!asset.sell_enabled}
                                    lines='none'
                                    onClick={() => presentSellPage()}>

                                    <IonIcon slot="start" src={cash} size='large' color={'secondary'} className="accountDetailsSellIcon"></IonIcon>
                                    <IonLabel>
                                        <IonText className="ion-text-wrap">

                                            <h2>
                                                {t('operations.Sell.Label')}
                                            </h2>
                                        </IonText>
                                        <IonNote>
                                            {t('operations.Sell.Label.description')}
                                        </IonNote>

                                    </IonLabel>
                                </IonItem>

                                {
                                    (assetToTrade && asset.trade_enabled) && (
                                        <IonItem lines='none' onClick={() => { goToTradePage() }}>

                                            <IonIcon slot="start" src={repeatOutline} size='large' color={'warning'}></IonIcon>
                                            <IonLabel>
                                                <IonText className="ion-text-wrap">

                                                    <h2>
                                                        {t('operations.Trade.Label')}
                                                    </h2>
                                                </IonText>
                                                <IonNote>
                                                    {t('operations.Trade.Label.description')}
                                                </IonNote>

                                            </IonLabel>
                                        </IonItem>
                                    )
                                }


                                <IonItem lines='none' onClick={() => { openQrModalPage() }}>

                                    <IonIcon slot="start" src={qrCodeOutline} size='large' color={'primary'}></IonIcon>
                                    <IonLabel>
                                        <IonText className="ion-text-wrap">

                                            <h2>
                                                {t('operations.Receive.Label')}

                                            </h2>
                                        </IonText>
                                        <IonNote>
                                            {t('operations.Receive.Label.description')}
                                        </IonNote>

                                    </IonLabel>
                                </IonItem>

                            </IonList>
                        </IonCardContent>
                    </IonCard>

                </IonContent>
            </>
        )
    }

    const [presentOperationComponent, dismissOperationComponent] = useIonModal(
        <OperationComponent
            closeAction={() => dismissOperationComponent()}
        />);



    const openOperationsPage = () => {

        // presentOperationComponent({
        //     initialBreakpoint:0.5,
        //     breakpoints: [0, 0.5, 0.5, 0.75],
        //   onDidDismiss:(()=>({}))
        // })

        presentOperationComponent({
            // presentingElement: pageRef.current,
            initialBreakpoint: 0.5,
            breakpoints: [0, 0.5, 0.5, 1],
            onDidDismiss: (() => ({}))
        })
    }

    // const goToTradePage = () => {

    //     const filteredAssets = supportedAssets.filter((a:Asset) => {
    //         if (cryptoMode) {
    //             return a.type === 'crypto' || a.type === 'fiat';
    //         } else {
    //             return a.type === 'fiat';
    //         }
    //     }).filter((a:Asset) => a.code !== asset.code);

    //     // Select an asset to trade based on the filtered list
    //     const toAsset = filteredAssets.length > 0 ? _.sample(filteredAssets) : null;// just a initial value that will be overwritten below



    //     dismissOperationComponent();
    //     nav?.push(() =>
    //         <TradePage
    //             assetTo={toAsset}
    //             assetFrom={asset}
    //         />
    //     )

    // }

    const goToTradePage = () => {

        const assetToTrade = findAssetToTrade();

        if (!assetToTrade) {
            presentErrorAlert({ message: 'No relevant assets available for trade.' });
            return;
        }

        dismissOperationComponent();

        // Navigate to TradePage with the relevant toAsset and fromAsset
        nav?.push(() =>
            <TradePage
                assetFrom={asset}
                assetTo={assetToTrade}
            />
        );
    };

    const openQrModalPage = () => {

        dismissOperationComponent();

        showQRReceive({
            presentingElement: pageRef.current
        })

    }

    const goToRecipientPage = () => {
        dispatch(resetPaymentDestinationInfo());
        nav?.push(() => <RecipientPage />);
    }

    const getBuyWidgetUrl = async () => {

        setIsSpinner(true)

        const params: BuyWidgetParams = {
            assetCode: asset.code,
            baseCurrencyCode: userInfo?.currency_code as string,
            provider: asset.providers['buy'][0],

        }


        try {

            const response = await dispatch(ebioroApi.endpoints.getBuyWidgetURL.initiate(params, { forceRefetch: true })).unwrap();
            await Browser.open({ url: response.url });
            setIsSpinner(false);


        } catch (error) {
            Sentry.captureException(error)
            setIsSpinner(false)
            presentErrorAlert(t('application.uncaught.ERROR'));
            return

        }
    }

    const getSellWidgetUrl = async () => {

        setIsSpinner(true);
        
        const params: SellWidgetParams = {
            assetCode: asset.code,
            baseCurrencyCode: userInfo?.currency_code as string,
            provider: asset.providers['sell'][0],
        }

        try {

            const response = await dispatch(ebioroApi.endpoints.getSellWidgetURL.initiate(params, { forceRefetch: true })).unwrap();
            await Browser.open({ url: response.url });
            setIsSpinner(false);

        } catch (error) {
            Sentry.captureException(error)
            setIsSpinner(false)
            presentErrorAlert(t('application.uncaught.ERROR'));
            return

        }

    }


    const presentBuyPage = async () => {

        dismissOperationComponent();
        await getBuyWidgetUrl();
    }

    const presentSellPage = async () => {

        dismissOperationComponent();
        await getSellWidgetUrl()

        // nav?.push(() =>
        //     <CryptoProvidersPage operation={Operation.SELL} />)

    }

    // const startVerification = async (userInfo: UserInfo) => {

    //     const verifyConfig = {
    //         email: userInfo.email,
    //         phone: userInfo.mobile_number,
    //     }

    //     try {

    //         const response = await dispatch(ebioroApi.endpoints.getKYCToken.initiate(registrationLevel as string)).unwrap(); // we are putting here just XLM as the default asset to start the verification flow
    //         setKycToken(response.accessToken);
    //         setKycTestEnvironment(response.testEnvironment);
    //         setVerificationConfig(verifyConfig);

    //         if (response) {

    //             Capacitor.getPlatform() === 'web' ? openVerificationScreen() : openNativeVerificationScreen(response)
    //         }

    //     } catch (error) {

    //         Sentry.captureException(error)

    //         presentErrorAlert({ message: t('application.uncaught.ERROR') })
    //         return

    //     }

    // }

    // const openVerificationScreen = async () => {

    //     setShowVerificationScreen(true);
    // }

    // const openNativeVerificationScreen = async (kycTokenResponse: any) => {

    //     try {

    //         SumsubPlugin.launch({ accessToken: kycTokenResponse.accessToken })

    //     } catch (error) {

    //         Sentry.captureException(error)

    //     }


    // }

    const getKYCToken = async () => {

        const { data: kycTokenResponse, error } = await dispatch(ebioroApi.endpoints.getKYCToken.initiate(registrationLevel as string));
        return kycTokenResponse.accessToken

    }

    useEffect(() => {
        dispatch(resetPaymentDestinationInfo());
    }, [])

    return (

        <IonPage ref={pageRef}>
            <IonHeader className="ion-no-border">
                <IonToolbar >
                    <IonButtons slot="start">
                        <IonButton onClick={() => { close() }} color='primary'>
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle className="ion-text-center"> {asset.name} </IonTitle>
                    <IonItem lines="none" slot="primary" className="accountDetailsPageHeaderItem"

                    >
                        <IonThumbnail className="accountsPage_Thumbnail_Flag" slot="start">
                            <img src={asset.icon} />
                        </IonThumbnail>
                    </IonItem>

                </IonToolbar>

            </IonHeader>

            <IonContent >

                <IonModal ref={unsupportedRegionModal}
                    isOpen={unsupportedRegionPage}
                >
                    <UnsupportedRegion
                        closeAction={() => { unsupportedRegionModal.current?.dismiss() }}
                    />

                </IonModal>
                <IonLoading isOpen={showLoading} />

                <IonCard >

                    <IonCardContent>


                        <IonItem lines="none">

                            <IonLabel>

                                <h4  >
                                    {formatConversion(assetBalance) + " " + asset.display_code}

                                </h4>
                            </IonLabel>

                        </IonItem>

                        <IonItem lines="none">
                            <IonText>

                                <h1>
                                    {formatConversion((assetBalance) * (assetFiatQuote as number)) + " " + userInfo?.currency_code}

                                </h1>

                            </IonText>
                        </IonItem>

                        <IonGrid>
                            <IonRow>
                                <IonCol size="6">
                                    <IonButton
                                        color='primary' fill="clear"
                                        onClick={() => { goToRecipientPage() }}
                                        disabled={!assetBalance || assetBalance == 0 ? true : false} >
                                        <IonIcon slot="start" icon={arrowForwardOutline} size='large' />
                                        <IonLabel>
                                            {t('accounts.details.SEND-BTN')}
                                        </IonLabel>
                                    </IonButton>
                                </IonCol>
                                <IonCol size="6">

                                    <IonButton fill="clear" color='primary' onClick={() => openOperationsPage()} >

                                        <IonIcon slot="start" icon={appsOutline} size='large' />
                                        {t('accounts.details.OPERATIONS-BTN')}

                                    </IonButton>


                                </IonCol>
                            </IonRow>
                        </IonGrid>

                    </IonCardContent>
                </IonCard>


                <MarketInfo asset={asset} />


                <IonModal
                    ref={verificationScreenModal}
                    isOpen={showVerificationScreen}
                >

                    <IonContent>

                        <IonHeader className="ion-no-border" >
                            <IonToolbar >
                                <IonButtons slot="start">
                                    <IonButton onClick={() => { setShowVerificationScreen(false) }}>
                                        <IonIcon slot="icon-only" icon={closeOutline} color="danger" />
                                    </IonButton>
                                </IonButtons>
                            </IonToolbar>

                        </IonHeader>

                        <SumsubWebSdk
                            accessToken={kycToken}
                            expirationHandler={getKYCToken}
                            config={verificationConfig}
                            onMessage={''}
                            onError={''}
                        />
                    </IonContent>

                </IonModal>

            </IonContent>
        </IonPage>

    )

}

// export default AccountDetailsPage

const AccountDetailsPage: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default AccountDetailsPage;